import type { ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { VisualAssetsRow } from "./VisualAssetsRow";

interface VisualAssetsTableProps {
  readonly titles: BulkEditTitle[];
  readonly selected: Set<string>;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onSelectAll: (selected: boolean) => void;
  readonly onFieldUpdate: (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | null,
  ) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onRefresh?: (titleId: string) => Promise<void>;
}

/**
 * Visual Assets table view for bulk editor
 * Displays poster, backdrop, logo with image management
 */
export function VisualAssetsTable({
  titles,
  selected,
  onSelect,
  onSelectAll,
  onFieldUpdate,
  onDelete,
  onDuplicate,
  onRefresh,
}: Readonly<VisualAssetsTableProps>): ReactElement {
  const allSelected = titles.length > 0 && titles.every((t) => selected.has(t.id));

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-900/50">
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={onSelectAll} aria-label="Select all" />
            </TableHead>
            <TableHead className="w-[250px] flex pt-[14px]">Title</TableHead>
            <TableHead className="w-[150px] text-center">Poster</TableHead>
            <TableHead className="w-[150px] text-center">Backdrop</TableHead>
            <TableHead className="w-[150px] text-center">Logo</TableHead>
            <TableHead className="w-[150px] text-center">H-Tile</TableHead>
            <TableHead className="w-[150px] text-center">V-Tile</TableHead>
            <TableHead className="w-[160px]">Source</TableHead>
            <TableHead className="w-[160px]">Status</TableHead>
            <TableHead className="w-[100px] text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {titles.map((title) => (
            <VisualAssetsRow
              key={title.id}
              title={title}
              isSelected={selected.has(title.id)}
              onSelect={onSelect}
              onFieldUpdate={onFieldUpdate}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onRefresh={onRefresh}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
